import React, { Component, ReactNode } from 'react'
import { RotateCw, Trash2 } from 'lucide-react'
import { captureComponentError } from '../../lib/telemetry'

const SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined)?.trim() ||
  'support@odonboard.com'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  isClearing: boolean
}

/**
 * App-level safe error boundary.
 * Preserves critical localStorage keys during cache-clear so users aren't
 * fully logged out unnecessarily.
 */
export class SafeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null, isClearing: false }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureComponentError(error, errorInfo, 'SafeErrorBoundary')
    this.setState({ errorInfo })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleClearCache = async () => {
    this.setState({ isClearing: true })
    try {
      if ('caches' in window) {
        const names = await caches.keys()
        await Promise.all(names.map(n => caches.delete(n)))
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        await Promise.all(regs.map(r => r.unregister()))
      }

      // Preserve critical localStorage keys so users stay signed in
      const criticalKeys = [
        'odonboard.lastEmail',
        'odonboard.currentTenantId',
        'odonboard.buildId',
      ]
      const preserved: Record<string, string> = {}
      criticalKeys.forEach(key => {
        const v = localStorage.getItem(key)
        if (v) preserved[key] = v
      })
      localStorage.clear()
      Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v))
      sessionStorage.clear()

      window.location.reload()
    } catch {
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand shadow-[0_4px_20px_rgba(31,42,68,0.18)]">
              <span className="text-xl font-bold text-white">OD</span>
            </div>

            <h1 className="mb-3 text-2xl font-semibold tracking-tight text-foreground">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
              The app encountered an unexpected error. Reloading usually fixes
              it — if not, clearing the cache should help.
            </p>

            <div className="flex flex-col items-center gap-3">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-[0_1px_3px_rgba(31,42,68,0.25)] transition-all duration-150 hover:bg-brand-hover"
              >
                <RotateCw className="h-4 w-4" />
                Reload page
              </button>
              <button
                onClick={this.handleClearCache}
                disabled={this.state.isClearing}
                className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-border bg-white px-5 py-2.5 text-sm font-medium text-foreground shadow-sm transition-all duration-150 hover:bg-panel disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {this.state.isClearing ? 'Clearing…' : 'Clear cache and reload'}
              </button>
            </div>

            {this.state.error && (
              <details className="mt-8 rounded-xl border border-border bg-white p-4 text-left">
                <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Error details
                </summary>
                <p className="mt-3 text-xs font-medium text-foreground break-words">
                  {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-meta leading-relaxed text-muted-foreground">
                    {this.state.error.stack}
                  </pre>
                )}
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-meta leading-relaxed text-[color:var(--color-text-tertiary)]">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </details>
            )}

            <p className="mt-6 text-xs text-muted-foreground">
              Need help?{' '}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-medium text-brand hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
